import { create } from 'zustand'
import { poems } from '../data/poems'

// Get poem count for a chapter (0-based index)
const getPoemCount = (chapterIndex) => {
  const chapterPoems = poems[chapterIndex + 1]
  return chapterPoems ? chapterPoems.length : 9
}

const TOTAL_CHAPTERS = 14

const useAppStore = create((set) => ({
  // Navigation state
  currentChapter: null, // null = overview, 0-13 = specific chapter
  currentPoem: 0, // 0-based poem index within chapter

  // Audio state
  audioPlaying: false,
  audioData: new Array(128).fill(0), // FFT frequency data
  audioVolume: 0.7,

  // UI state
  showUI: true,
  showChapterList: false,
  hoveredChapter: null,

  // Mobile UI state
  mobileNavOpen: false, // Whether mobile navigation drawer is open
  mobileAudioExpanded: false, // Whether audio controls are expanded on mobile

  // Track if user has had first audio interaction (for browser autoplay policy)
  hasAudioInteraction: false,
  setHasAudioInteraction: () => set({ hasAudioInteraction: true }),

  // Track if AudioContext has been started (after user gesture)
  audioContextStarted: false,
  setAudioContextStarted: () => set({ audioContextStarted: true }),

  // Actions
  setCurrentChapter: (chapter) => set((state) => ({
    currentChapter: chapter,
    currentPoem: 0,
    audioPlaying: chapter !== null && state.hasAudioInteraction ? true : state.audioPlaying
  })),
  setCurrentPoem: (poem) => set({ currentPoem: poem }),

  nextPoem: () => set((state) => {
    if (state.currentChapter === null) return {}
    const count = getPoemCount(state.currentChapter)
    return { currentPoem: (state.currentPoem + 1) % count }
  }),

  prevPoem: () => set((state) => {
    if (state.currentChapter === null) return {}
    const count = getPoemCount(state.currentChapter)
    return { currentPoem: state.currentPoem === 0 ? count - 1 : state.currentPoem - 1 }
  }),

  nextChapter: () => set((state) => {
    const next = state.currentChapter === null ? 0 : (state.currentChapter + 1) % TOTAL_CHAPTERS
    return {
      currentChapter: next,
      currentPoem: 0,
      audioPlaying: state.hasAudioInteraction ? true : state.audioPlaying
    }
  }),

  prevChapter: () => set((state) => {
    const prev = state.currentChapter === null ? TOTAL_CHAPTERS - 1 :
                 state.currentChapter === 0 ? null :
                 state.currentChapter - 1
    return {
      currentChapter: prev,
      currentPoem: 0,
      audioPlaying: prev !== null && state.hasAudioInteraction ? true : state.audioPlaying
    }
  }),

  toggleAudio: () => set((state) => ({
    audioPlaying: !state.audioPlaying,
    // Mark that user has interacted with audio (enables auto-play on chapter changes)
    hasAudioInteraction: true
  })),
  setAudioData: (data) => set({ audioData: data }),
  setAudioVolume: (volume) => set({ audioVolume: volume }),

  toggleUI: () => set((state) => ({ showUI: !state.showUI })),
  toggleChapterList: () => set((state) => ({ showChapterList: !state.showChapterList })),
  setHoveredChapter: (chapter) => set({ hoveredChapter: chapter }),

  // Mobile UI actions
  toggleMobileNav: () => set((state) => ({ mobileNavOpen: !state.mobileNavOpen })),
  closeMobileNav: () => set({ mobileNavOpen: false }),
  openMobileNav: () => set({ mobileNavOpen: true }),
  toggleMobileAudio: () => set((state) => ({ mobileAudioExpanded: !state.mobileAudioExpanded })),

  // Reset to overview
  resetToOverview: () => set({
    currentChapter: null,
    currentPoem: 0,
    showChapterList: false,
    mobileNavOpen: false
  })
}))

export default useAppStore
